import { z } from 'zod';
import { analysisUrlSchema } from './analysis.ts';
import { createSyntheticTestSchema, type Mission, type PersonaDto } from './synthetic.ts';
import type { SiteKind } from './site-kind.ts';
import type { SiteLanguage } from './markets.ts';

/**
 * Before a new test, the AI reads the site once: what it offers and to whom, its language and country, a
 * short name for the test, three visitors who fit it and the main thing they would come to do. The new
 * test opens with all of it filled in; everything stays editable. Reused for 7 days per start page.
 */

/** How long a reading of a site is reused before the new test reads it again. */
export const SITE_ANALYSIS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Visitors proposed for a new test: one per persona written for the site. */
export const SITE_ANALYSIS_PERSONAS = 3;

/** What is stored of a reading (the personas are saved in the library, by id). */
export interface StoredSiteAnalysis {
  testName: string;
  language: SiteLanguage | null;
  /** Country the visitors come from (ISO code). */
  market: string | null;
  kind: SiteKind;
  /** What the site offers and to whom, in 2-3 sentences: the context of personas drafted later. */
  summary: string;
  mission: Mission | null;
  /** The pages could be read and the AI wrote the reading (false: guessed from the address, ready-made visitors). */
  read: boolean;
}

export interface SiteAnalysisDto extends StoredSiteAnalysis {
  siteUrl: string;
  host: string;
  /** The start page the reading is for (`/`, `/pricing`). */
  startPath: string;
  /** The proposed visitors, already in the organization's personas. */
  personas: PersonaDto[];
  analyzedAt: string;
  /** Reused from an earlier reading (less than 7 days old). */
  cached: boolean;
}

export const siteAnalysisSchema = z.object({
  url: analysisUrlSchema,
  /** Read the site again even when a recent reading exists. */
  refresh: z.boolean().optional(),
});
export type SiteAnalysisInput = z.input<typeof siteAnalysisSchema>;

/**
 * A new test from anywhere, with or without a project: without `projectId` the site's project is found (same
 * domain) or created within the plan's projects, and the person confirms they may test the site.
 */
export const startTestSchema = createSyntheticTestSchema.and(
  z.object({
    projectId: z.uuid().optional(),
    /** ALTCHA proof of work, for the first test of an account (when switched on). */
    altcha: z.string().max(10_000).optional(),
  }),
);
export type StartTestInput = z.input<typeof startTestSchema>;

export interface StartTestResultDto {
  projectId: string;
  testId: string;
  projectCreated: boolean;
}
